/**
 * Configuration Component - Device configuration management page
 * 
 * Features:
 * - Manual device configuration with form validation
 * - Bulk device upload via Excel files with template download
 * - Device search and modification capabilities
 * - Upload history tracking with pagination
 * - Site/Location dropdown integration
 * - Real-time form validation and error handling
 * - Device MAC address change tracking
 * - Excel template generation and validation
 * 
 * @author CTS - Ford PM UI Team
 
 */

import React, { useMemo, useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { useAuth } from '../../context/AuthContext';
import { uploadFileApi, getDeviceConfigApi, getUploadHistoryApi, updateDeviceConfigApi, getJobStatusApi } from '../../api/configurationApi';
import { getSitesApi } from '../../api/dashboardApi';
import { FullScreenLoader } from '../../components';
import { copyToClipboard } from '../../utils/clipboardUtils.jsx';

// Material UI imports
import {
  Typography,
  MenuItem,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormHelperText,
  Box,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ContentCopy,
} from '@mui/icons-material';

// Custom styled components
import {
  ConfigContainer,
  ConfigOuterContainer,
  ConfigPageHeader,
  ConfigModeBox,
  ConfigModeButton,
  ConfigPanel,
  ConfigFormGrid,
  ConfigTextField,
  ConfigSelect,
  ConfigFormControl,
  ConfigActionBox,
  ConfigPrimaryButton,
  ConfigSecondaryButton,
  ConfigFileInputBox,
  ConfigSuccessAlert,
  ConfigErrorAlert,
  ConfigInfoText,
  ConfigTableContainer,
  ConfigFieldContainer,
  ConfigLoadStatus,
  ConfigSectionHeader,
  ConfigTemplateInfo,
  ConfigHistorySection,
  ConfigPaginationContainer,
  ConfigTablePagination,
  ConfigJobStatusDialog,
  ConfigJobStatusDialogTitle,
  ConfigJobStatusDialogContent,
  ConfigJobStatusErrorTable,
  ConfigJobStatusDialogActions,
  ConfigStatusCheckButton,
  ConfigInProgressButton,
  DeviceDetailRowContainer,
  StatusCard,
  StatusCardsContainer,
  ConfigCheckboxFieldContainer,
  ConfigCheckboxContainer,
  ConfigReplaceFieldContainer,
  ConfigFormControlLabel,
} from './Configuration.styles';
import DateUtils from '../../utils/DateUtils.jsx';
import { AppLabels } from '../../constant/constant';

/**
 * Safe logging utility that only logs in development mode
 * Prevents console statements from appearing in production builds
 */
const safeLog = {
  info: (message, ...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Configuration] ${message}`, ...args);
    }
  },
  error: (message, ...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[Configuration] ${message}`, ...args);
    }
  },
  warn: (message, ...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[Configuration] ${message}`, ...args);
    }
  }
};



// ========================================
// CONSTANTS AND CONFIGURATION
// ========================================

/**
 * Configuration constants for pagination, file size limits, and UI settings
 */
const CONFIG_CONSTANTS = {
  PAGINATION: {
    DEFAULT_ROWS_PER_PAGE: 10,
    ROWS_PER_PAGE_OPTIONS: [5, 10, 25, 50, 100],
    INITIAL_PAGE: 0
  },
  FILE_UPLOAD: {
    MAX_FILE_SIZE_MB: 50,
    TEMPLATE_GENERATION_DELAY: 800, // milliseconds
    XL_ALLOWED_ROWS: 50
  },
  UI: {
    LOADING_SPINNER_SIZE: 20,
    MIN_INPUT_WIDTH: 350,
    MAX_REPLACE_FIELD_WIDTH: 300,
    TOOLTIP_MAX_HEIGHT: 400,
    TOOLTIP_MAX_WIDTH: 700,
    TABLE_CELL_PADDING: 20,
    MARGIN_BOTTOM: 16,
    FONT_SIZE_SMALL: 14,
    FONT_SIZE_EXTRA_SMALL: 12,
    FONT_SIZE_TINY: '0.75rem',
    BUTTON_PADDING: '4px 8px',
    MAX_ALLOWED_CHAR: 500
  },
  HTTP_STATUS: {
    NOT_FOUND: 404,
    BAD_REQUEST: 400,
    INTERNAL_SERVER_ERROR: 500
  },
  RADIX: 10 // For parseInt operations
};

/**
 * Excel template column definitions for bulk upload
 * These columns must match the expected API format
 */
const TEMPLATE_COLUMNS = [
  "Location", "Sitename", "Floor", "Model", "Cabinet/Rack", 
  "Single Power Source Devices", "Dual Power Source Devices","Plug Location", "SP Device Name", 
  "SP Device ID", "SP Device SSID", 
  "SSID","Password", "StaticIP",
  "Modem SP Device ID", "Modem SP Device SSID"
];


/**
 * Required fields that cannot be empty during validation
 * Used for both manual form and bulk upload validation
 */
const REQUIRED_FIELDS = [
  "Location", "Sitename", "Floor","Model" , "Cabinet/Rack", "Single Power Source Devices", "Dual Power Source Devices", "Plug Location", "SP Device ID", "SP Device SSID", "Modem SP Device ID", "Modem SP Device SSID"
];

const NOT_REQUIRED_FIELDS = new Set([
 "Model", "Cabinet/Rack", "Single Power Source Devices","Dual Power Source Devices"
]);

/**
 * Initial form values for device configuration
 * Defines the structure and default values for the form
 */
const initialValues = {
  "store_plug_id": "",
  "store_name": "",
  "location_name": "",
  "store_id": "",
  "location_id": "",
  "device_mac": "",
  "model": "",
  "device_mac_changed_flag": false,
  "new_device_mac": "",
  "smart_plug_location": "",
  'critical_devices': "",
  'dualcritical_devices': "",
  'sp_DeviceName': "",
  'modemId': ''
}

// Form field configuration for dynamic rendering
const FORM_FIELDS = [
  {
    key: 'store_plug_id',
    label: AppLabels.configurationLabels.storePlugIdLabel,
    type: 'text',
    readOnly: true,
    placeholder: AppLabels.configurationLabels.autoGeneratedPlaceholder,
    required: true
  },
  {
    key: 'device_mac',
    label: AppLabels.configurationLabels.deviceMacLabel,
    type: 'text',
    readOnly: true,
  },
  {
    key: 'store_name',
    label: AppLabels.configurationLabels.storeNameLabel,
    type: 'text',
    required: true,
    readOnly: true,
  },
  {
    key: 'location_name',
    label: AppLabels.configurationLabels.locationNameLabel,
    type: 'text',
    required: true,
    readOnly: true,
  },
  {
    key: 'sp_DeviceName',
    label: AppLabels.configurationLabels.deviceNameLabel,
    type: 'text',
    readOnly: true,
  },
  {
    key: 'smart_plug_location',
    label: AppLabels.configurationLabels.smartPlugLocation,
    type: 'text',
    readOnly: true,
  },
   {
    key: 'critical_devices',
    label: AppLabels.configurationLabels.criticalDevices,
    type: 'text',
    readOnly: false,
  },
   {
    key: 'dualcritical_devices',
    label: AppLabels.configurationLabels.dualCriticalDevices,
    type: 'text',
    readOnly: false,
  },
  {
    key: 'modemId',
    label: 'Modem Device ID',
    type: 'text',
    required: true,
    readOnly: true,
  }
];


/**
 * Configuration Main Component - Device configuration management interface
 * 
 * Provides two modes:
 * - Bulk Mode: Excel file upload with template download and validation
 * - Manual Mode: Individual device configuration with form validation
 * 
 * @returns {JSX.Element} Configuration page component
 */
export default function Configuration() {
  // ========================================
  // STATE MANAGEMENT
  // ========================================


  const [history, setHistory] = useState([]); // Upload history data
  const { user } = useAuth(); // Current authenticated user
  const email = user?.email || '';

  // Sites data state
  const [sitesData, setSitesData] = useState({ AllSites: [] });
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState("");

  // Refs to track API call states and prevent duplicate calls
  const sitesApiCalled = useRef(false);
  const historyApiCalled = useRef(false);

  // Bulk upload state
  const [file, setFile] = useState(null);

  // Manual mode state
  const [manualId, setManualId] = useState("");
  const [deviceFoundInAPI, setDeviceFoundInAPI] = useState(false);

  const [form, setForm] = useState(initialValues);
  const [loaded, setLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [bulkError, setBulkError] = useState("");
  const [bulkSuccess, setBulkSuccess] = useState("");
  const [fullErrorDetails, setFullErrorDetails] = useState("");
  const [manualLoadError, setManualLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [manualError, setManualError] = useState("");
  const [manualSuccess, setManualSuccess] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);

  const [mode, setMode] = useState(user?.isCognizantUser ? "bulk" : "manual"); // Current mode: 'bulk' or 'manual'

  // Pagination state
  const [page, setPage] = useState(CONFIG_CONSTANTS.PAGINATION.INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(CONFIG_CONSTANTS.PAGINATION.DEFAULT_ROWS_PER_PAGE);

  // Job status dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [jobStatusLoading, setJobStatusLoading] = useState(false);
  const [jobResult, setJobResult] = useState(null);

  // Calculate paginated history data
  const paginatedHistory = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return history.slice(startIndex, endIndex);
  }, [history, page, rowsPerPage]);

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, CONFIG_CONSTANTS.RADIX));
    setPage(CONFIG_CONSTANTS.PAGINATION.INITIAL_PAGE); // Reset to first page when changing rows per page
  };


  // Fetch upload history from API
  const fetchUploadHistory = async () => {
    setHistoryLoading(true);
    try {
      const historyData = await getUploadHistoryApi();

      // Filter history data to only show records for current user
      // const userHistoryData = historyData.filter(item =>
      //   item.uploadedBy && item.uploadedBy.toLowerCase() === email.toLowerCase()
      // );

      // Transform API response to match our history format
      const transformedHistory = historyData.map(item => ({
        uploadedAt: DateUtils.formatDateWithLocalZone(item.uploadedAt),
        uploadedBy: item.uploadedBy,
        fileName: item.fileName,
        jobId: item.jobId,
        blobUrl: item.blobUrl,
        executionStatus: item.executionStatus,
        processStatus: item.processStatus,
        error: item.error,
        uploadId: item.uploadId
      }));

      setHistory(transformedHistory);
    } catch (error) {
      safeLog.error('Error fetching upload history:', error);
      // Keep existing local history if API fails
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch sites data on component mount
  useEffect(() => {
    const loadSitesData = async () => {
      if (sitesApiCalled.current) return; // Prevent duplicate calls
      sitesApiCalled.current = true;

      setSitesLoading(true);
      setSitesError(""); // Clear previous errors
      try {
        const sitesResponse = await getSitesApi();
        setSitesData(sitesResponse);
      } catch (error) {
        safeLog.error('Error fetching sites data:', error);
        setSitesError(AppLabels.configurationLabels.failedToLoadSites);
        sitesApiCalled.current = false; // Reset on error to allow retry
      } finally {
        setSitesLoading(false);
      }
    };

    loadSitesData();
  }, []);

  // Fetch upload history when email becomes available
  useEffect(() => {
    if (email && !historyApiCalled.current) {
      historyApiCalled.current = true;
      fetchUploadHistory().catch(() => {
        historyApiCalled.current = false; // Reset on error to allow retry
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Depend on user object, not email string

  // Reset form and messages when switching modes
  useEffect(() => {
    if (mode !== "bulk") {
      setBulkError("");
      setBulkSuccess("");
    }
    if (mode !== "manual") {
      setManualLoadError("");
      setManualError("");
      setManualSuccess("");
    }
    setIsLoading(false);
    setLoadingMessage("");
  }, [mode]);


  const downloadTemplate = async () => {
    setIsLoading(true);
    setLoadingMessage(AppLabels.configurationLabels.generatingTemplateMessage);

    try {
      await new Promise(resolve => setTimeout(resolve, CONFIG_CONSTANTS.FILE_UPLOAD.TEMPLATE_GENERATION_DELAY));

      const sample = [{
        "Location": "Chennai",
        "Sitename": "GTBC",
        "Floor": 2,
        "Model": 'Model',
        "Cabinet/Rack": "Cabinet",
        "Single Power Source Devices": "prcrt123, prcrt234",
        "Dual Power Source Devices": "prcrt01, prcrt02",
        "Plug Location": "PDU",
        "SP Device Name": "PRC-SP001-PDU",
        "SP Device ID": "C049EF8875E4",
        "SP Device SSID": "shellyplugus-C049EF8875E4",
        "SSID": "wifi name",
        "Password": "wifi",  // NOSONAR
        "StaticIP": "shelly static ip",
        "Modem SP Device ID": "C049EF8875E7",
        "Modem SP Device SSID": "shellyplugus-C049EF8875E7"
      }];

      const ws = XLSX.utils.json_to_sheet(sample, { header: TEMPLATE_COLUMNS });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, AppLabels.configurationLabels.templateSheetName);
      XLSX.writeFile(wb, AppLabels.configurationLabels.templateFilename);
    } catch (error) {
      safeLog.error('Error downloading template:', error);
      setBulkError(AppLabels.configurationLabels.downloadTemplateFailed);
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  // Helper function to validate file basic properties
  const validateFileBasics = (file) => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      throw new Error(AppLabels.configurationLabels.invalidFileFormat);
    }
    const maxSizeBytes = CONFIG_CONSTANTS.FILE_UPLOAD.MAX_FILE_SIZE_MB * 1024 * 1024;  // 50MB in bytes

    if (file.size > maxSizeBytes) {
      throw new Error(AppLabels.configurationLabels.fileSizeTooLarge.replace('1$', CONFIG_CONSTANTS.FILE_UPLOAD.MAX_FILE_SIZE_MB));
    }
  };

  // Helper function to validate workbook structure
  const validateWorkbookStructure = (workbook) => {
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
      throw new Error(AppLabels.configurationLabels.fileEmptyOrCorrupted);
    }

    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (jsonData.length === 0) {
      throw new Error(AppLabels.configurationLabels.fileEmpty);
    }

    if (jsonData.length < 2) {
      throw new Error(AppLabels.configurationLabels.fileNoData);
    }

    return jsonData;
  };

  // Helper function to validate headers
  const validateHeaders = (headers) => {
    const missingColumns = [];
   
    // Check for missing required columns
    REQUIRED_FIELDS.forEach(col => {
      if (!headers.includes(col)) {
        missingColumns.push(col);
      }
    });


    if (missingColumns.length > 0) {
      let errorMessage = `Missing required columns: ${missingColumns.join(", ")}. Please use the template format.`;
      throw new Error(errorMessage);
    }
  };

  // Helper function to validate required fields in a row
  const validateRequiredFields = (row, headers, rowNumber, errors) => {
    REQUIRED_FIELDS.forEach(field => {
      const columnIndex = headers.indexOf(field);
      if (columnIndex !== -1 && !NOT_REQUIRED_FIELDS.has(field)) {
        const cellValue = row[columnIndex];
        if (cellValue === undefined || cellValue === null || cellValue.toString().trim() === '') {
          errors.push(`Row ${rowNumber}: ${field} ${AppLabels.configurationLabels.requiredFieldEmpty}`);
        }
      }
    });
  };

  // Helper function to validate device ID duplicates
  const validateDeviceId = (row, headers, rowNumber, deviceSPIDValues, errors) => {
    const deviceSPIDIndex = headers.indexOf('SP Device ID');
    if (deviceSPIDIndex === -1 || !row[deviceSPIDIndex]) {
      return;
    }

    const deviceSPID = row[deviceSPIDIndex].toString().trim();

    if (deviceSPID.length === 0) {
      errors.push(`Row ${rowNumber}: ${AppLabels.configurationLabels.spDeviceIdEmpty}`);
      return;
    }

    if (deviceSPIDValues.has(deviceSPID.toLowerCase())) {
      errors.push(`Row ${rowNumber}: SP Device ID "${deviceSPID}" ${AppLabels.configurationLabels.spDeviceIdDuplicated}`);
    } else {
      deviceSPIDValues.add(deviceSPID.toLowerCase());
    }
  };

  // Helper function to validate device SSID duplicates
  const validateDeviceSSID = (row, headers, rowNumber, deviceSPSSIDValues, errors) => {
    const deviceSPSSIDIndex = headers.indexOf('SP Device SSID');
    if (deviceSPSSIDIndex === -1 || !row[deviceSPSSIDIndex]) {
      return;
    }

    const deviceSPSSID = row[deviceSPSSIDIndex].toString().trim();

    if (deviceSPSSID.length === 0) {
      errors.push(`Row ${rowNumber}: ${AppLabels.configurationLabels.spDeviceSSIDEmpty}`);
      return;
    }

    if (deviceSPSSIDValues.has(deviceSPSSID.toLowerCase())) {
      errors.push(`Row ${rowNumber}: SP Device SSID "${deviceSPSSID}" ${AppLabels.configurationLabels.spDeviceSSIDDuplicated}`);
    } else {
      deviceSPSSIDValues.add(deviceSPSSID.toLowerCase());
    }
  };
   // Helper function to validate device name duplicates
  const validateDeviceName = (row, headers, rowNumber, deviceNameValues, errors) => {
    const spDeviceNameIndex = headers.indexOf('SP Device Name');
    if (spDeviceNameIndex === -1 || !row[spDeviceNameIndex]) {
      return;
    }

    const spDeviceName = row[spDeviceNameIndex].toString().trim();

    if (spDeviceName.length === 0) {
      errors.push(`Row ${rowNumber}: ${AppLabels.configurationLabels.spDeviceSSIDEmpty}`);
      return;
    }

    if (deviceNameValues.has(spDeviceName.toLowerCase())) {
      errors.push(`Row ${rowNumber}: SP Device Name "${spDeviceName}" ${AppLabels.configurationLabels.spDeviceSSIDDuplicated}`);
    } else {
      deviceNameValues.add(spDeviceName.toLowerCase());
    }
  };

  // Helper function to validate dual devices for a specific Modem SSID
  const validateDualDevicesForModemSSID = (rowEntries, errors) => {
    // Group rows by their actual dual device combinations within this modem SSID
    const deviceCombinationMap = new Map();
    
    rowEntries.forEach(entry => {
      const deviceKey = entry.devices
        .map(device => device.toLowerCase().trim())
        .sort()
        .join(',');
      
      if (!deviceCombinationMap.has(deviceKey)) {
        deviceCombinationMap.set(deviceKey, []);
      }
      deviceCombinationMap.get(deviceKey).push(entry);
    });
    
    console.log('entry data deviceCombinationMap', deviceCombinationMap);
    
    // Validate each unique dual device combination within this modem SSID
    deviceCombinationMap.forEach((deviceEntries, deviceKey) => {
      validateSingleDualDeviceCombination(deviceEntries, deviceKey, errors);
    });
  };

  // Helper function to validate a single dual device combination
  const validateSingleDualDeviceCombination = (deviceEntries, deviceKey, errors) => {
    if (deviceEntries.length === 1) {
      // Single row with dual devices is invalid - dual devices need a pair
      const singleRow = deviceEntries[0].rowNumber;
      errors.push(`Row ${singleRow}: Dual Power Source Devices "${deviceKey}" appear in only 1 row. Dual devices require exactly 2 rows as a pair.`);
    } else if (deviceEntries.length > 2) {
      // More than 2 rows with same dual devices is invalid
      const affectedRows = deviceEntries.map(entry => entry.rowNumber).join(', ');
      errors.push(`Row ${affectedRows}: Dual Power Source Devices "${deviceKey}" appear in ${deviceEntries.length} rows (maximum allowed: 2 rows).`);
    }
    // If exactly 2 rows, it's valid - no error needed
  };

  // Helper function to validate dual power source device limits per Modem SP Device SSID
  const validateDualPowerSourceLimits = (dataRows, headers, errors) => {
    const modemSSIDToRowsMap = new Map();
    
    const dualPowerSourceIndex = headers.indexOf('Dual Power Source Devices');
    const modemSSIDIndex = headers.indexOf('Modem SP Device SSID');
    const plugLocationIndex = headers.indexOf('Plug Location');
    
    if (dualPowerSourceIndex === -1 || modemSSIDIndex === -1 || plugLocationIndex === -1) {
      return; // Skip validation if required columns are not found
    }
    
    dataRows.forEach((row, index) => {
      const rowNumber = index + 2;
      
      // Check if row has data
      const hasData = row.some(cell => cell !== undefined && cell !== null && cell.toString().trim() !== '');
      if (!hasData) return;
      
      const dualPowerSourceDevices = row[dualPowerSourceIndex];
      const modemSSID = row[modemSSIDIndex];
      const plugLocation = row[plugLocationIndex];
      
      // Skip if either field is empty
      if (!dualPowerSourceDevices || !modemSSID || 
          dualPowerSourceDevices.toString().trim() === '' || 
          modemSSID.toString().trim() === '') {
        return;
      }
      
      const modemSSIDStr = modemSSID.toString().trim();
      const dualDevicesStr = dualPowerSourceDevices.toString().trim();
      const plugLocationStr = plugLocation ? plugLocation.toString().trim().toLowerCase() : '';
      
      // Check if this row has dual power source devices (non-empty after trimming)
      const dualDevicesList = dualDevicesStr.split(',')
        .map(device => device.trim())
        .filter(device => device !== '');
      
      // Only count rows that actually have dual power source devices
      if (dualDevicesList.length > 0) {
        // Validate that dual power source devices are only allowed for PDU plug locations
        if (plugLocationStr !== 'pdu') {
          errors.push(`Row ${rowNumber}: Dual Power Source Devices are only applicable for Plug Location "PDU". Current Plug Location: "${plugLocation || 'empty'}"`);
          return;
        }
        
        if (!modemSSIDToRowsMap.has(modemSSIDStr)) {
          modemSSIDToRowsMap.set(modemSSIDStr, []);
        }
        
        // Add row information for error reporting
        modemSSIDToRowsMap.get(modemSSIDStr).push({
          rowNumber,
          devices: dualDevicesList
        });
      }
    });
    // Check dual device requirements for each Modem SP Device SSID
    modemSSIDToRowsMap.forEach((rowEntries, modemSSID) => {
      validateDualDevicesForModemSSID(rowEntries, errors);
    });
  };

  // Helper function to get column value safely
  const getColumnValue = (row, headers, columnName) => {
    const columnIndex = headers.indexOf(columnName);
    if (columnIndex === -1) return null;
    
    const value = row[columnIndex];
    if (value === undefined || value === null) return null;
    
    return value.toString().trim();
  };

  // Helper function to validate a single critical device field
  const validateSingleCriticalDeviceField = (row, headers, rowNumber, columnName, fieldType, displayName, errors) => {
    const valueStr = getColumnValue(row, headers, columnName);
    
    if (!valueStr || valueStr === '') return;
    
    const validationError = validateCriticalDevicesFormat(valueStr, fieldType);
    if (validationError) {
      errors.push(`Row ${rowNumber}: ${displayName} - ${validationError}`);
    }
  };

  // Helper function to validate critical devices fields in Excel upload with reduced complexity
  const validateCriticalDevicesInRow = (row, headers, rowNumber, errors) => {
    // Validate Single Power Source Devices
    validateSingleCriticalDeviceField(
      row, 
      headers, 
      rowNumber, 
      'Single Power Source Devices', 
      'critical_devices', 
      'Single Power Source Devices', 
      errors
    );

    // Validate Dual Power Source Devices
    validateSingleCriticalDeviceField(
      row, 
      headers, 
      rowNumber, 
      'Dual Power Source Devices', 
      'dualcritical_devices', 
      'Dual Power Source Devices', 
      errors
    );
  };

  // Helper function to validate a single row
  const validateDataRow = (row, validationContext) => {
    const { headers, rowNumber, deviceSPIDValues, deviceSPSSIDValues, deviceNameValues, errors, emptyRows } = validationContext;
    
    // Check if entire row is empty
    const hasData = row.some(cell => cell !== undefined && cell !== null && cell.toString().trim() !== '');
    if (!hasData) {
      emptyRows.push(rowNumber);
      return;
    }

    validateRequiredFields(row, headers, rowNumber, errors);
    validateDeviceId(row, headers, rowNumber, deviceSPIDValues, errors);
    validateDeviceSSID(row, headers, rowNumber, deviceSPSSIDValues, errors);
    validateDeviceName(row, headers, rowNumber, deviceNameValues, errors);
    validateCriticalDevicesInRow(row, headers, rowNumber, errors);
  };

  // Helper function to process validation results
  const processValidationResults = (errors, dataRows, emptyRows, deviceSPIDValues) => {
    const validDataRows = dataRows.filter((row, index) => {
      const rowNumber = index + 2;
      return !emptyRows.includes(rowNumber);
    });

    if (validDataRows.length === 0) {
      throw new Error(AppLabels.configurationLabels.noValidDataRows);
    }

    if (errors.length > 0) {
      const fullErrorList = errors.join('\n');

      // Extract error message construction logic
      let errorMessage;
      if (errors.length > 5) {
        // Show first 5 errors with summary
        const firstFiveErrors = errors.slice(0, 5).join('\n');
        const remainingErrorsCount = errors.length - 5;
        errorMessage = `${AppLabels.configurationLabels.foundValidationErrors} ${errors.length} ${AppLabels.configurationLabels.validationErrors}\n${firstFiveErrors}\n...and ${remainingErrorsCount} ${AppLabels.configurationLabels.andMoreErrors}`;
      } else {
        // Show all errors with appropriate singular/plural label
        const errorLabel = errors.length > 1
          ? AppLabels.configurationLabels.validationErrorPlural
          : AppLabels.configurationLabels.validationError;
        errorMessage = `${AppLabels.configurationLabels.foundValidationErrors} ${errors.length} ${errorLabel}\n${fullErrorList}`;
      }

      // Create a custom error that includes both the summary message and full details
      const error = new Error(errorMessage + '\n\n--- FULL_ERROR_DETAILS_START ---\n' + fullErrorList + '\n--- FULL_ERROR_DETAILS_END ---');
      throw error;
    }

    return {
      isValid: true,
      rowCount: validDataRows.length,
      emptyRowCount: emptyRows.length,
      uniqueDevices: deviceSPIDValues.size,
      message: `File validation successful. Found ${validDataRows.length} valid data rows with ${deviceSPIDValues.size} unique devices.`
    };
  };

  // Main file validation function using modern Blob methods
  const validateUploadedFile = async (file) => {
    try {
      validateFileBasics(file);

      // Use modern Blob.arrayBuffer() instead of FileReader
      const arrayBuffer = await file.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });

      const jsonData = validateWorkbookStructure(workbook);
      const headers = jsonData[0].map(header => header?.toString().trim());
      const dataRows = jsonData.slice(1);

      // Validate row count limit (50 rows maximum)
      if (dataRows.length > CONFIG_CONSTANTS.FILE_UPLOAD.XL_ALLOWED_ROWS) {
        throw new Error(`${AppLabels.configurationLabels.fileRowLimitExceeded.replace('1$', CONFIG_CONSTANTS.FILE_UPLOAD.XL_ALLOWED_ROWS)} (Found ${dataRows.length} rows)`);
      }

      validateHeaders(headers);

      const errors = [];
      const emptyRows = [];
      const deviceSPIDValues = new Set();
      const deviceSPSSIDValues = new Set();
      const deviceNameValues = new Set();

      dataRows.forEach((row, index) => {
        const rowNumber = index + 2;
        const validationContext = {
          headers,
          rowNumber,
          deviceSPIDValues,
          deviceSPSSIDValues,
          deviceNameValues,
          errors,
          emptyRows
        };
        validateDataRow(row, validationContext);
      });

      // Validate dual power source device limits per Modem SP Device SSID
      validateDualPowerSourceLimits(dataRows, headers, errors);

      const result = processValidationResults(errors, dataRows, emptyRows, deviceSPIDValues);
      return result;

    } catch (parseError) {
      safeLog.error('File parsing error:', parseError);
      const errorMessage = parseError.message || 'Failed to read the Excel file. Please ensure it is a valid .xlsx file and try again.';
      throw new Error(errorMessage);
    }
  };

  // Event handlers
  const handleBulkUpload = async () => {
    setBulkError("");
    setBulkSuccess("");
    setFullErrorDetails("");

    if (!file) {
      setBulkError(AppLabels.configurationLabels.selectExcelFileError);
      return;
    }

    // Validate file before upload
    setIsLoading(true);
    setLoadingMessage(AppLabels.configurationLabels.validatingFileMessage);

    try {
      const validationResult = await validateUploadedFile(file);
      safeLog.info('File validation result:', validationResult);

      // Update loading message for upload
      setLoadingMessage(AppLabels.configurationLabels.uploadingFileMessage);

      const response = await uploadFileApi(file, email, 'bulk');

      if (response?.success) {
        setIsLoading(false);
        setLoadingMessage("");
        setBulkSuccess(`File ${file.name} uploaded successfully!, Please check the file status in the History table.`);

        // Refresh history to show the latest upload
        fetchUploadHistory();
      } else {
        setBulkError(response?.message || AppLabels.configurationLabels.uploadFailedMessage);
        setIsLoading(false);
        setLoadingMessage("");
      }
    } catch (error) {
      safeLog.error("Upload error:", error);

      // Check if this is a validation error or API error
      if (error.message && (
        error.message.includes('Invalid file format') ||
        error.message.includes('File size too large') ||
        error.message.includes('Missing required columns') ||
        error.message.includes('validation error') ||
        error.message.includes('validation errors') ||
        error.message.includes('Additional headers found') ||
        error.message.includes('empty') ||
        error.message.includes('corrupted') ||
        error.message.includes('File validation successful.')
      )) {
        // This is a validation error
        // Extract the summary message and full details from the error message
        const fullMessage = error.message;
        const fullDetailsStartIndex = fullMessage.indexOf('--- FULL_ERROR_DETAILS_START ---');
        const fullDetailsEndIndex = fullMessage.indexOf('--- FULL_ERROR_DETAILS_END ---');
        
        let summaryMessage = fullMessage;
        let fullDetails = fullMessage;
        
        if (fullDetailsStartIndex !== -1 && fullDetailsEndIndex !== -1) {
          // Extract the summary part (everything before the full details section)
          summaryMessage = fullMessage.substring(0, fullDetailsStartIndex).trim();
          // Extract the full details part
          const startPos = fullDetailsStartIndex + '--- FULL_ERROR_DETAILS_START ---\n'.length;
          fullDetails = fullMessage.substring(startPos, fullDetailsEndIndex).trim();
        }
        
        setBulkError(summaryMessage);
        setFullErrorDetails(fullDetails);
      } else if (error.response) {
        // This is an API error
        setBulkError(`Upload failed: ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        setBulkError(AppLabels.configurationLabels.networkErrorMessage);
      } else {
        setBulkError(error.message || AppLabels.configurationLabels.unexpectedErrorMessage);
      }

      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const handleManualLoad = async () => {
    setManualLoadError("");
    setErrors({});
    setManualError("");
    setManualSuccess("");



    if (!manualId.trim()) {
      setManualLoadError(AppLabels.configurationLabels.enterDeviceMacError);
      return;
    }

    setIsLoading(true);
    setLoadingMessage(AppLabels.configurationLabels.loadingDeviceMessage);

    try {
      try {
        const deviceConfig = await getDeviceConfigApi(manualId.trim());
        if (deviceConfig) {
          // Map API response to form structure
          
           const criticalDevicesList = deviceConfig.criticaldevices?.map(cd => cd.critical_id).filter(id => id && id.trim() !== '').join(', ') ?? AppLabels.notApplicable;
          const dualCriticalDevicesList = deviceConfig.dualcriticaldevices?.map(cd => cd.dual_critical_id).filter(id => id && id.trim() !== '').join(', ') ?? AppLabels.notApplicable;
          
          setForm({
            store_plug_id: deviceConfig.location_plug_id || "",
            store_name: deviceConfig.store_name || "",
            location_name: deviceConfig.location_name || "",
            store_id: deviceConfig.store_id || "",
            location_id: deviceConfig.location_id || "",
            device_mac: deviceConfig.device_mac || "",
            model: deviceConfig.model ?? AppLabels.notApplicable,
            // isModem: deviceConfig.isModem || false,
            device_mac_changed_flag: false, // Default to false for loaded data
            new_device_mac: "", // Default empty for loaded data
            smart_plug_location: deviceConfig.smart_plug_location ?? AppLabels.notApplicable,
            critical_devices: criticalDevicesList,
            dualcritical_devices: dualCriticalDevicesList,
            sp_DeviceName: deviceConfig.sp_DeviceName.toUpperCase(),
            modemId: deviceConfig.modemId
          });
          setDeviceFoundInAPI(true);
          setLoaded(true);
        } else {
          setDeviceFoundInAPI(false);
          setManualLoadError(AppLabels.configurationLabels.errorLoadingDevice);
          setLoaded(true);
        }
      } catch (apiError) {
        safeLog.error('API error while loading device config:', apiError);
        setDeviceFoundInAPI(false);
        setLoaded(true);
      }
    } catch (error) {
      safeLog.error('Error loading device:', error);
      setManualLoadError(AppLabels.configurationLabels.errorLoadingDevice);
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  // Helper function to validate critical devices format
  const validateCriticalDevicesFormat = (value, fieldName) => {
    if (!value || value.trim() === '') {
      return null; // Empty values are allowed
    }

    const trimmedValue = value.trim();
    
    // Check maximum length
    if (trimmedValue.length > CONFIG_CONSTANTS.UI.MAX_ALLOWED_CHAR) {
      return fieldName === 'critical_devices' 
         ? AppLabels.configurationLabels.criticalDevicesTooLong.replace('1$', CONFIG_CONSTANTS.UI.MAX_ALLOWED_CHAR)
        : AppLabels.configurationLabels.dualCriticalDevicesTooLong.replace('1$', CONFIG_CONSTANTS.UI.MAX_ALLOWED_CHAR);
    }

    // Check for valid format: alphanumeric, spaces, commas, hyphens, underscores, plus signs, parentheses only
    const validFormatRegex = /^[a-zA-Z0-9\s,_+\-().]+$/;
    if (!validFormatRegex.test(trimmedValue)) {
      return fieldName === 'critical_devices'
        ? AppLabels.configurationLabels.criticalDevicesInvalid
        : AppLabels.configurationLabels.dualCriticalDevicesInvalid;
    }

    // Check for empty values between commas (e.g., "device1,,device2" or "device1, ,device2")
    // Only validate if there are actual device names (not just commas)
    const devices = trimmedValue.split(',').map(device => device.trim());
    const nonEmptyDevices = devices.filter(device => device !== '');
    
    // Only check for empty devices if there are some non-empty devices
    if (nonEmptyDevices.length > 0) {
      const hasEmptyDevices = devices.some(device => device === ''); //NOSONAR
      if (hasEmptyDevices) {
        return fieldName === 'critical_devices'
          ? AppLabels.configurationLabels.criticalDevicesInvalid
          : AppLabels.configurationLabels.dualCriticalDevicesInvalid;
      }
    }

    return null; // No validation errors
  };

  // Helper function to validate form data
  const validateFormData = () => {
    const newErrors = {};

    if (!form.store_name.trim()) newErrors.store_name = AppLabels.configurationLabels.storeNameRequired;
    if (!form.location_name.trim()) newErrors.location_name = AppLabels.configurationLabels.locationNameRequired;
    if (!form.model.trim()) newErrors.model = AppLabels.configurationLabels.modelRequired;
    if (!form.modemId.trim()) newErrors.modemId = 'Modem ID is required';

    // Validate Replace Plug ID when checkbox is enabled
    if (form.device_mac_changed_flag && !form.new_device_mac.trim()) {
      newErrors.new_device_mac = AppLabels.configurationLabels.replaceDeviceMacRequired;
    }

    // Validate critical devices fields
    const criticalDevicesError = validateCriticalDevicesFormat(form.critical_devices, 'critical_devices');
    if (criticalDevicesError) {
      newErrors.critical_devices = criticalDevicesError;
    }

    const dualCriticalDevicesError = validateCriticalDevicesFormat(form.dualcritical_devices, 'dualcritical_devices');
    if (dualCriticalDevicesError) {
      newErrors.dualcritical_devices = dualCriticalDevicesError;
    }

    return newErrors;
  };

  // Helper function to handle API response errors
  const handleApiError = (error) => {
    safeLog.error("Manual save error:", error);

    if (error.response) {
      const statusCode = error.response.status;
      const errorMessage = error.response.data?.message || '';
      
      if (statusCode === CONFIG_CONSTANTS.HTTP_STATUS.NOT_FOUND || statusCode === CONFIG_CONSTANTS.HTTP_STATUS.BAD_REQUEST) {
        setManualError(errorMessage || `The device "${form.device_mac || 'specified'}" could not be found in the system.`);
      } else if (statusCode === CONFIG_CONSTANTS.HTTP_STATUS.INTERNAL_SERVER_ERROR) {
        // Check if it's a critical devices database error
        if (errorMessage.includes('CriticalDevice') && errorMessage.includes('must be manually assigned')) {
          setManualError(AppLabels.configurationLabels.criticalDevicesDatabaseError);
        } else {
          setManualError(`${errorMessage || error.response.statusText}`);
        }
      } else {
        setManualError(`${errorMessage || error.response.statusText}`);
      }
    } else if (error.request) {
      setManualError(AppLabels.configurationLabels.networkErrorSave);
    } else {
      setManualError(error.message || AppLabels.configurationLabels.unexpectedSaveError);
    }
  };

  const handleManualSave = async () => {
    if (!loaded) return;

    // Clear previous states
    setErrors({});
    setManualError("");
    setManualSuccess("");

    // Validate form data
    const validationErrors = validateFormData();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Start loading
    setIsLoading(true);
    setLoadingMessage(deviceFoundInAPI ? AppLabels.configurationLabels.updatingDeviceMessage : AppLabels.configurationLabels.creatingDeviceMessage);

    try {
      const response = await updateDeviceConfigApi(form, email);
      if (response?.success) {
        setManualSuccess(AppLabels.configurationLabels.deviceMacupdated);
        setErrors({});
        fetchUploadHistory();
      } else {
        setManualError(response?.message || AppLabels.configurationLabels.saveDeviceFailedMessage);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const handleManualEntryAction = () => {
    setForm(initialValues);
    setMode("manual");
    setLoaded(false);
    setManualId("");
    setDeviceFoundInAPI(false);
  };

  // Helper function to validate character limits for critical devices fields
  const validateCharacterLimit = (field, value) => {
    if ((field === 'critical_devices' || field === 'dualcritical_devices') && value.length > CONFIG_CONSTANTS.UI.MAX_ALLOWED_CHAR) {
      const errorMessage = field === 'critical_devices' 
        ? AppLabels.configurationLabels.criticalDevicesTooLong.replace('1$', CONFIG_CONSTANTS.UI.MAX_ALLOWED_CHAR)
        : AppLabels.configurationLabels.dualCriticalDevicesTooLong.replace('1$', CONFIG_CONSTANTS.UI.MAX_ALLOWED_CHAR);
      setErrors(prev => ({ ...prev, [field]: errorMessage }));
      return false; // Validation failed
    }
    return true; // Validation passed
  };

  // Helper function to handle store_name field changes
  const handleStoreNameChange = (value, prevForm) => {
    const selectedSite = sitesData.AllSites.find(site => site.store_name === value);
    const updates = {
      store_id: selectedSite ? selectedSite.store_id : '',
      location_name: '',
      location_id: ''
    };
    
    // Only reset modem ID if smart plug location is NOT "Modem"
    if (prevForm.smart_plug_location?.toLowerCase() !== 'modem') {
      updates.modemId = '';
    }
    
    return updates;
  };

  // Helper function to handle location_name field changes
  const handleLocationNameChange = (value, prevForm) => {
    const selectedSite = sitesData.AllSites.find(site => site.store_name === prevForm.store_name);
    const updates = {};
    
    if (selectedSite) {
      const selectedLocation = selectedSite.locations.find(location => location.location_name === value);
      updates.location_id = selectedLocation ? selectedLocation.location_id : '';
      
      // Only reset modem ID if smart plug location is NOT "Modem"
      if (prevForm.smart_plug_location?.toLowerCase() !== 'modem') {
        updates.modemId = '';
      }
    }
    
    return updates;
  };

  // Helper function to handle device_mac_changed_flag field changes
  const handleDeviceMacFlagChange = (value) => {
    if (value) {
      return {};
    } else {
      return { new_device_mac: '' };
    }
  };

  // Helper function to handle form field updates
  const updateFormFields = (field, value, prevForm) => {
    const newForm = { ...prevForm, [field]: value };
    
    if (field === 'store_name') {
      Object.assign(newForm, handleStoreNameChange(value, prevForm));
    } else if (field === 'location_name') {
      Object.assign(newForm, handleLocationNameChange(value, prevForm));
    } else if (field === 'device_mac_changed_flag') {
      Object.assign(newForm, handleDeviceMacFlagChange(value));
    }
    
    return newForm;
  };

  // Helper function to handle validation and error clearing
  const handleValidationAndErrors = (field, value) => {
    // Real-time validation for critical devices fields
    if (field === 'critical_devices' || field === 'dualcritical_devices') {
      const validationError = validateCriticalDevicesFormat(value, field);
      setErrors(prev => ({ ...prev, [field]: validationError || "" }));
    } else if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }

    // Clear related errors based on field changes
    if (field === 'store_name' && (errors.location_name || errors.location_id || errors.modemId)) {
      setErrors(prev => ({ ...prev, location_name: "", location_id: "", modemId: "" }));
    }

    if (field === 'location_name' && errors.modemId) {
      setErrors(prev => ({ ...prev, modemId: "" }));
    }

    if (field === 'device_mac_changed_flag' && !value && errors.new_device_mac) {
      setErrors(prev => ({ ...prev, new_device_mac: "" }));
    }
  };

  const handleFormChange = (field, value) => {
    // Validate character limits for critical devices fields
    if (!validateCharacterLimit(field, value)) {
      return; // Don't update if validation fails
    }

    // Update form fields with appropriate logic
    setForm(prev => updateFormFields(field, value, prev));

    // Handle validation and error clearing
    handleValidationAndErrors(field, value);
  };

  // Helper function to get available locations for selected store
  const getAvailableLocations = useMemo(() => {
    if (!form.store_name || !sitesData.AllSites) return [];

    const selectedSite = sitesData.AllSites.find(site => site.store_name === form.store_name);
    return selectedSite ? selectedSite.locations || [] : [];
  }, [sitesData, form.store_name]);

  // Helper function to get available modems for selected location
  const getAvailableModems = useMemo(() => {
    if (!form.location_name || !sitesData.AllSites) return [];

    const selectedSite = sitesData.AllSites.find(site => site.store_name === form.store_name);
    if (!selectedSite) return [];

    const selectedLocation = selectedSite.locations.find(location => location.location_name === form.location_name);
    return selectedLocation ? selectedLocation.modem_sp_device_ssid || [] : [];
  }, [sitesData, form.store_name, form.location_name]);

  // Helper function to get dynamic form fields based on mode
  const getDynamicFormFields = () => {
    return FORM_FIELDS.map(field => {
      // For store_name field in manual mode, make it a select
      if (field.key === 'store_name' && mode === 'manual') {
        return {
          ...field,
          type: 'select',
          readOnly: false,
          options: sitesData.AllSites.map(site => ({
            value: site.store_name,
            label: site.store_name
          }))
        };
      }

      // For location_name field in manual mode, make it a select
      if (field.key === 'location_name' && mode === 'manual') {
        return {
          ...field,
          type: 'select',
          readOnly: false,
          options: getAvailableLocations.map(location => ({
            value: location.location_name,
            label: location.location_name
          }))
        };
      }

      // For modemId field in manual mode
      if (field.key === 'modemId' && mode === 'manual') {
        // If Smart Plug Location is "Modem", show as text field (read-only)
        if (form.smart_plug_location?.toLowerCase() === 'modem') {
          return {
            ...field,
            type: 'text',
            readOnly: true,
            required: true
          };
        }
        // Otherwise, show as select dropdown
        return {
          ...field,
          type: 'select',
          readOnly: false,
          required: true,
          options: getAvailableModems.map(modem => ({
            value: modem,
            label: modem
          }))
        };
      }

      return field;
    });
  };

  // Job status checking function
  const handleJobStatusCheck = async (event, jobId) => {
    setDialogOpen(true);
    setJobStatusLoading(true);
    setJobResult(null);

    try {
      // Call the API directly with jobId
      const statusResponse = await getJobStatusApi(jobId);

      // Use the actual API response structure
      setJobResult({
        status: statusResponse.executionstatus || 'UNKNOWN',
        message: statusResponse.message || `Job is ${(statusResponse.executionstatus || 'unknown').toLowerCase()}`,
        submitted_at: statusResponse.uploaded_at || statusResponse.updated_at,
        updated_at: statusResponse.updated_at,
        uploaded_count: statusResponse.uploaded_count ?? 0,
        failed_count: statusResponse.failed_count ?? 0,
        processed_rows_count: statusResponse.processed_rows_count ?? 0,
        job_id: statusResponse.job_id,
        uploaded_by: statusResponse.uploaded_by,
        errors: statusResponse.errors ?? [] // Include errors array from API response
      });
    } catch (error) {
      safeLog.error('Error checking job status:', error);
      setJobResult({
        error: true,
        message: AppLabels.configurationLabels.failedToCheckJobStatus
      });
    } finally {
      setJobStatusLoading(false);
    }
  };

  // Close job status dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setJobResult(null);
  };

  // History table column configuration
  const HISTORY_TABLE_COLUMNS = [
    {
      key: 'uploadId',
      header: 'Upload ID',
      render: (row) => row.uploadId,
      hideColumn: false,
      width: '8%'
    },
    {
      key: 'uploadedAt',
      header: 'Upload Time',
      render: (row) => row.uploadedAt,
      hideColumn: false,
      width: '18%'
    },
    {
      key: 'uploadedBy',
      header: 'Uploaded By',
      render: (row) => row.uploadedBy,
      hideColumn: false,
      width: '15%'
    },
    {
      key: 'fileName',
      header: 'File Name / Plug ID',
      render: (row) => row.fileName,
      hideColumn: false,
      width: '32%'
    },
    {
      key: 'jobId',
      header: 'File Status Check',
      render: (row) => {
        // Check for error conditions first
        if (row.executionStatus === "FAILED" && row.processStatus === "ERROR") {
          return <span style={{ color: '#ef4444' }}>{row.error}</span>;
        }
        
        if (row.executionStatus === "PENDING" && row.processStatus === "UPLOADED") {
          return (
            <ConfigInProgressButton disabled>
              In Progress
            </ConfigInProgressButton>
          );
        }

        // Check for success conditions to show button
        if (row.executionStatus === "COMPLETED" && 
            row.processStatus === "SUCCESS" && 
            row.blobUrl && 
            row.jobId !== null) {
          return (
            <ConfigStatusCheckButton
              onClick={(event) => handleJobStatusCheck(event, row.jobId)}
            >
              {AppLabels.configurationLabels.checkStatusButton}
            </ConfigStatusCheckButton>
          );
        }
        
        if (row.jobId === null && row.executionStatus === null && row.processStatus === null) {
          return <span>{AppLabels.configurationLabels.deviceMacupdated}</span>;
        }
        
        // Default case - show NA
        return <span>{AppLabels.notApplicable}</span>;
      },
      hideColumn: false,
      width: '27%'
    }
  ];

  // Filter visible columns based on hideColumn property
  const getVisibleHistoryColumns = () => {
    return HISTORY_TABLE_COLUMNS.filter(column => !column.hideColumn);
  };


  // Helper function to determine field disable conditions
  const getFieldDisableConditions = (key) => {
    const shouldDisableDualCritical = (key === 'dualcritical_devices' || key === 'critical_devices') && 
      (form.smart_plug_location?.toLowerCase() === 'modem');
    const shouldDisableModemId = key === 'modemId' && 
      (form.smart_plug_location?.toLowerCase() === 'modem');
    
    return { shouldDisableDualCritical, shouldDisableModemId };
  };

  // Helper function to get helper text for disabled fields
  const getDisabledFieldHelperText = (key) => {
    return key === 'dualcritical_devices' 
      ? AppLabels.configurationLabels.dualCriticalDevicesDisabledForModem 
      : AppLabels.configurationLabels.singleCriticalDevicesDisabledForModem;
  };

  // Helper function to render select field
  const renderSelectField = (field, shouldDisableModemId) => {
    const { key, label, options } = field;
    
    return (
      <ConfigFieldContainer key={key}>
        <ConfigFormControl size="small" error={!!errors[key]}>
          <InputLabel>{label}</InputLabel>
          <ConfigSelect
            value={form[key]}
            onChange={(e) => handleFormChange(key, e.target.value)}
            disabled={isLoading || shouldDisableModemId}
            error={!!errors[key]}
            label={label}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </ConfigSelect>
          {(errors[key] || shouldDisableModemId) && (
            <FormHelperText>
              {errors[key] || (shouldDisableModemId ? AppLabels.configurationLabels.modemIdDisabledForModem : '')}
            </FormHelperText>
          )}
        </ConfigFormControl>
      </ConfigFieldContainer>
    );
  };

  // Helper function to render checkbox field
  const renderCheckboxField = (field) => {
    const { key, label } = field;
    
    return (
      <ConfigFieldContainer key={key}>
        <FormControlLabel
          control={
            <Checkbox
              checked={form[key] || false}
              onChange={(e) => handleFormChange(key, e.target.checked)}
              disabled={isLoading}
              sx={{
                color: '#4f7cff',
                '&.Mui-checked': {
                  color: '#4f7cff',
                },
              }}
            />
          }
          label={label}
          sx={{
            color: '#e0e6ed',
            '& .MuiFormControlLabel-label': {
              fontSize: `${CONFIG_CONSTANTS.UI.FONT_SIZE_SMALL}px`,
            },
          }}
        />
        {errors[key] && (
          <FormHelperText error>
            {errors[key]}
          </FormHelperText>
        )}
      </ConfigFieldContainer>
    );
  };

  // Helper function to calculate rows for critical devices field
  const calculateCriticalDevicesRows = (value) => {
    const deviceCount = value && value.trim() !== '' 
      ? value.split(',').map(d => d.trim()).filter(d => d !== '').length 
      : 0;
    
    const minRows = 1;
    const maxRows = 6;
    return Math.min(Math.max(Math.ceil(deviceCount / 3), minRows), maxRows);
  };

  // Helper function to render critical devices field
  const renderCriticalDevicesField = (field, shouldDisableDualCritical, slotPropsConfig) => {
    const { key, label } = field;
    const criticalDevicesValue = form[key] || '';
    const toolTipTitle = key === 'critical_devices' ? 'Copy Single Power Source Devices' : 'Copy Dual Power Source Devices';
    const criticalDevicesPlaceholder = key === 'critical_devices' 
      ? AppLabels.configurationLabels.criticalDevicesPlaceholder 
      : AppLabels.configurationLabels.dualCriticalDevicesPlaceholder;
    const calculatedRows = calculateCriticalDevicesRows(criticalDevicesValue);
    const helperText = getDisabledFieldHelperText(key);

    return (
      <ConfigFieldContainer key={key}>
        <Box sx={{ position: 'relative' }}>
          <ConfigTextField
            label={label}
            value={criticalDevicesValue}
            onChange={(e) => handleFormChange(key, e.target.value)}
            error={!!errors[key]}
            helperText={errors[key] || (shouldDisableDualCritical ? helperText : '')}
            disabled={isLoading}
            slotProps={slotPropsConfig}
            size="small"
            placeholder={criticalDevicesPlaceholder}
            multiline
            rows={calculatedRows}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                paddingRight: '40px', // Space for copy icon
              }
            }}
          />
          {(criticalDevicesValue && criticalDevicesValue.trim() !== '') && (
            <Tooltip title={toolTipTitle} arrow>
              <IconButton
                onClick={(event) => copyToClipboard(criticalDevicesValue, event)}
                sx={{
                  position: 'absolute',
                  right: '8px',
                  top: '8px',
                  padding: '4px',
                  color: '#9aa3c7',
                  '&:hover': {
                    color: '#4f7cff',
                  }
                }}
                size="small"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </ConfigFieldContainer>
    );
  };

  // Helper function to render default text field
  const renderDefaultTextField = (field, shouldDisableModemId, slotPropsConfig) => {
    const { key, label, placeholder } = field;
    
    return (
      <ConfigFieldContainer key={key}>
        <ConfigTextField
          label={label}
          value={form[key]}
          onChange={(e) => handleFormChange(key, e.target.value)}
          error={!!errors[key]}
          helperText={errors[key] || (shouldDisableModemId ? AppLabels.configurationLabels.modemIdDisabledForModem : '')}
          disabled={isLoading}
          slotProps={slotPropsConfig}
          size="small"
          placeholder={placeholder}
        />
      </ConfigFieldContainer>
    );
  };

  // Main helper function to render form fields dynamically
  const renderFormField = (field) => {
    const { key, type, readOnly } = field;
    const { shouldDisableDualCritical, shouldDisableModemId } = getFieldDisableConditions(key);
    
    // Create slotProps object for readonly fields
    const slotPropsConfig = readOnly || shouldDisableDualCritical || shouldDisableModemId
      ? { input: { readOnly: true } } 
      : {};

    // Route to appropriate renderer based on field type
    if (type === 'select') {
      return renderSelectField(field, shouldDisableModemId);
    }

    if (type === 'checkbox') {
      return renderCheckboxField(field);
    }

    if (key === 'critical_devices' || key === 'dualcritical_devices') {
      return renderCriticalDevicesField(field, shouldDisableDualCritical, slotPropsConfig);
    }

    return renderDefaultTextField(field, shouldDisableModemId, slotPropsConfig);
  };

  return (
    <ConfigContainer>
      <ConfigOuterContainer>
        <ConfigPageHeader>
          <Typography variant="h2" component="h2">
            {AppLabels.configurationLabels.configurationPageTitle}
          </Typography>
          <Typography className="subtitle">
            {user?.isCognizantUser ? AppLabels.configurationLabels.configurationSubtitle : AppLabels.configurationLabels.configurationSubtitleSearch }
          </Typography>
        </ConfigPageHeader>

        {/* Mode Selection */}
        <ConfigModeBox>
          {user?.isCognizantUser && 
          <ConfigModeButton
            selected={mode === "bulk"}
            onClick={() => setMode("bulk")}
            disabled={isLoading}
            startIcon={<CloudUploadIcon />}
          >
            {AppLabels.configurationLabels.bulkUploadExcelMode}
          </ConfigModeButton>
          }
          
          <ConfigModeButton
            selected={mode === "manual"}
            onClick={handleManualEntryAction}
            disabled={isLoading}
            startIcon={<SearchIcon />}
          >
            {AppLabels.configurationLabels.manualUpdateMode}
          </ConfigModeButton>
        </ConfigModeBox>

        {/* Bulk Upload Mode */}
        {user?.isCognizantUser && mode === "bulk" && (
          <ConfigPanel elevation={0}>
            <Typography variant="h3" component="h3">
              {AppLabels.configurationLabels.bulkUploadTitle}
            </Typography>
            <Typography component="p">
              {AppLabels.configurationLabels.bulkUploadDescription}
            </Typography>

            <ConfigFileInputBox>
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => {
                  setBulkError("");
                  setBulkSuccess("");
                  setFullErrorDetails("");
                  setFile(e.target.files[0]);
                }}
                onClick={(e) => {
                  // Reset the input value to allow selecting the same file again
                  e.target.value = null;
                  setBulkError("");
                  setBulkSuccess("");
                  setFullErrorDetails("");
                }}
                disabled={isLoading}
              />
              <ConfigPrimaryButton
                onClick={handleBulkUpload}
                disabled={isLoading}
                startIcon={<CloudUploadIcon />}
              >
                {AppLabels.configurationLabels.uploadExcelButton}
              </ConfigPrimaryButton>
              <ConfigPrimaryButton
                onClick={downloadTemplate}
                disabled={isLoading}
                startIcon={<DownloadIcon />}
              >
                {AppLabels.configurationLabels.downloadTemplateButton}
              </ConfigPrimaryButton>
            </ConfigFileInputBox>

            <ConfigTemplateInfo>
              <span className="template-title">{AppLabels.configurationLabels.expectedColumnsLabel}
                 <span className="template-columns">
                {REQUIRED_FIELDS.join(" | ")}
              </span>
              </span>
             
            </ConfigTemplateInfo>
            {bulkError && (
              <ConfigErrorAlert severity="error">
                {((bulkError.includes('Found ') && bulkError.includes('validation errors')) ||
                  bulkError.includes('Missing required columns') ||
                  bulkError.includes('Additional headers found')) && fullErrorDetails ? (
                  <Tooltip
                    title={
                      <Box sx={{ maxHeight: `${CONFIG_CONSTANTS.UI.TOOLTIP_MAX_HEIGHT}px`, overflow: 'auto', whiteSpace: 'pre-line' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: `${CONFIG_CONSTANTS.UI.FONT_SIZE_EXTRA_SMALL}px` }}>
                          {bulkError.includes('validation errors') ? 'All validation errors:' : 'Full error details:'}{'\n'}{fullErrorDetails}
                        </Typography>
                      </Box>
                    }
                    placement="bottom-start"
                    arrow
                    interactive
                    slotProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: '#1e1e1e',
                          color: '#ffffff',
                          maxWidth: `${CONFIG_CONSTANTS.UI.TOOLTIP_MAX_WIDTH}px`,
                          fontSize: `${CONFIG_CONSTANTS.UI.FONT_SIZE_EXTRA_SMALL}px`,
                          '& .MuiTooltip-arrow': {
                            color: '#1e1e1e',
                          },
                        },
                      },
                    }}
                  >
                    <Box sx={{ cursor: 'help', display: 'inline' }}>
                      {bulkError}
                    </Box>
                  </Tooltip>
                ) : (
                  bulkError
                )}
              </ConfigErrorAlert>
            )}
            {bulkSuccess && (
              <ConfigSuccessAlert severity="success">
                {bulkSuccess}
              </ConfigSuccessAlert>
            )}
          </ConfigPanel>
        )}

        {/* Manual Mode */}
        {mode === "manual" && (
          <ConfigPanel elevation={0}>
            <Typography variant="h3" component="h3">
              {AppLabels.configurationLabels.manualUpdateTitle}
            </Typography>

            {sitesLoading && (
              <ConfigInfoText>{AppLabels.configurationLabels.loadingSitesData}</ConfigInfoText>
            )}

            {sitesError && (
              <ConfigErrorAlert severity="error">
                {sitesError}
              </ConfigErrorAlert>
            )}

            {/* ID Input and Load */}
            <ConfigActionBox>
              <ConfigTextField
                label={AppLabels.configurationLabels.enterDeviceMacLabel}
                value={manualId}
                onChange={(e) => {
                  setLoaded(false);
                  setDeviceFoundInAPI(false);
                  setManualId(e.target.value);
                  setManualLoadError("");
                  setManualError("");
                  setManualSuccess("");
                  setErrors({});
                  setForm(initialValues);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && manualId.trim() && !isLoading) {
                    handleManualLoad();
                  }
                }}
                error={!!manualLoadError}
                disabled={isLoading}
                size="small"
                sx={{ minWidth: CONFIG_CONSTANTS.UI.MIN_INPUT_WIDTH }}
              />
              <ConfigPrimaryButton
                onClick={handleManualLoad}
                disabled={isLoading || !!sitesError}
                startIcon={<SearchIcon />}
              >
                {AppLabels.configurationLabels.loadButton}
              </ConfigPrimaryButton>
              {loaded && (
                <ConfigLoadStatus>
                  {deviceFoundInAPI ? AppLabels.configurationLabels.loadedExistingRecord : AppLabels.configurationLabels.deviceMacNotFound}
                </ConfigLoadStatus>
              )}
            </ConfigActionBox>

            {/* Form Fields */}
            {loaded && deviceFoundInAPI && (
              <>
                <ConfigSectionHeader>
                  {AppLabels.configurationLabels.deviceDetailsHeader}
                </ConfigSectionHeader>

                <ConfigFormGrid>
                  {getDynamicFormFields().map(renderFormField)}

                  {/* Checkbox and Replace Plug ID in same row */}
                  <ConfigCheckboxFieldContainer>
                    <ConfigCheckboxContainer>
                      <ConfigFormControlLabel>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={form.device_mac_changed_flag || false}
                              onChange={(e) => handleFormChange('device_mac_changed_flag', e.target.checked)}
                              disabled={isLoading}
                              sx={{
                                color: '#4f7cff',
                                '&.Mui-checked': {
                                  color: '#4f7cff',
                                },
                              }}
                            />
                          }
                          label={AppLabels.configurationLabels.enableReplaceLabel}
                        />
                      </ConfigFormControlLabel>
                    </ConfigCheckboxContainer>

                    {/* Conditionally render Replace Plug ID field when checkbox is enabled */}
                    {form.device_mac_changed_flag && (
                      <ConfigReplaceFieldContainer style={{ maxWidth: `${CONFIG_CONSTANTS.UI.MAX_REPLACE_FIELD_WIDTH}px` }}>
                        <ConfigTextField
                          label={AppLabels.configurationLabels.replaceDeviceMacLabel}
                          value={form.new_device_mac}
                          onChange={(e) => handleFormChange('new_device_mac', e.target.value)}
                          error={!!errors.new_device_mac}
                          helperText={errors.new_device_mac}
                          disabled={isLoading}
                          size="small"
                          placeholder={AppLabels.configurationLabels.replaceDeviceMacPlaceholder}
                          fullWidth
                        />
                      </ConfigReplaceFieldContainer>
                    )}
                  </ConfigCheckboxFieldContainer>
                </ConfigFormGrid>

                <ConfigActionBox>
                  <ConfigPrimaryButton
                    onClick={handleManualSave}
                    disabled={isLoading}
                    startIcon={<SaveIcon />}
                  >
                    {deviceFoundInAPI ? AppLabels.configurationLabels.updateButton : AppLabels.configurationLabels.createButton}
                  </ConfigPrimaryButton>
                  <ConfigSecondaryButton
                    onClick={() => {
                      setLoaded(false);
                      setManualId("");
                      setErrors({});
                      setDeviceFoundInAPI(false);
                      setManualLoadError("");
                      setManualError("");
                      setManualSuccess("");
                    }}
                    disabled={isLoading}
                    startIcon={<RefreshIcon />}
                  >
                    {AppLabels.configurationLabels.resetButton}
                  </ConfigSecondaryButton>
                </ConfigActionBox>

                {manualLoadError && (
                  <ConfigErrorAlert severity="error">
                    {manualLoadError}
                  </ConfigErrorAlert>
                )}

                {manualError && (
                  <ConfigErrorAlert severity="error">
                    {manualError}
                  </ConfigErrorAlert>
                )}
                {manualSuccess && (
                  <ConfigSuccessAlert severity="success">
                    {manualSuccess}
                  </ConfigSuccessAlert>
                )}
              </>
            )}
          </ConfigPanel>
        )}

        {/* History Table */}
        <ConfigHistorySection>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <Typography variant="h3" component="h3">
              {AppLabels.configurationLabels.uploadHistoryTitle}
            </Typography>
            <ConfigPrimaryButton
              onClick={() => {
                historyApiCalled.current = false; // Reset to allow refresh
                fetchUploadHistory();
              }}
              disabled={historyLoading}
              startIcon={<RefreshIcon />}
              size="small"
            >
              Refresh
            </ConfigPrimaryButton>
          </Box>
          <ConfigTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {getVisibleHistoryColumns().map(column => (
                    <TableCell 
                      key={column.key}
                      sx={{ width: column.width, minWidth: column.width }}
                    >
                      {column.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  if (historyLoading) {
                    return [
                      <TableRow key="loading">
                        <TableCell colSpan={getVisibleHistoryColumns().length} style={{ textAlign: 'center', padding: `${CONFIG_CONSTANTS.UI.TABLE_CELL_PADDING}px` }}>
                          {AppLabels.configurationLabels.loadingHistory}
                        </TableCell>
                      </TableRow>
                    ];
                  }

                  if (history.length === 0) {
                    return [
                      <TableRow key="no-data">
                        <TableCell colSpan={getVisibleHistoryColumns().length} style={{ textAlign: 'center', padding: `${CONFIG_CONSTANTS.UI.TABLE_CELL_PADDING}px` }}>
                          {AppLabels.configurationLabels.noUploadHistory}
                        </TableCell>
                      </TableRow>
                    ];
                  }

                  return paginatedHistory.map((h) => (
                    <TableRow key={h.id}>
                      {getVisibleHistoryColumns().map(column => (
                        <TableCell 
                          key={column.key}
                          sx={{ width: column.width, minWidth: column.width }}
                        >
                          {column.render(h)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </ConfigTableContainer>

          {/* Pagination Component */}
          {history.length > CONFIG_CONSTANTS.PAGINATION.DEFAULT_ROWS_PER_PAGE && !historyLoading && (
            <ConfigPaginationContainer>
              <ConfigTablePagination
                component="div"
                count={history.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={CONFIG_CONSTANTS.PAGINATION.ROWS_PER_PAGE_OPTIONS}
                showFirstButton
                showLastButton
              />
            </ConfigPaginationContainer>
          )}
        </ConfigHistorySection>

        {/* Full Screen Loader */}
        <FullScreenLoader
          isVisible={isLoading}
          message={loadingMessage || AppLabels.configurationLabels.loadingMessage}
          size="medium"
        />

        {/* Job Status Dialog */}
        <ConfigJobStatusDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <ConfigJobStatusDialogTitle>
            <Typography variant="body1">
              {AppLabels.configurationLabels.jobStatusTitle}
            </Typography>
            <IconButton
              className="close-button"
              onClick={handleCloseDialog}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </ConfigJobStatusDialogTitle>

          <ConfigJobStatusDialogContent>
            {(() => {
              if (jobStatusLoading) {
                return (
                  <Box className="loading-container">
                    <CircularProgress size={CONFIG_CONSTANTS.UI.LOADING_SPINNER_SIZE} />
                    <Typography>{AppLabels.configurationLabels.checkingJobStatusText}</Typography>
                  </Box>
                );
              }

              if (!jobResult) {
                return null;
              }

              if (jobResult.error) {
                return (
                  <Box>
                    <Typography className="error-text">{jobResult.message}</Typography>
                  </Box>
                );
              }

              if (jobResult.uploaded_count >= 0) {
                // Show final result
                return (
                  <Box>
                    <Box className="status-section">
                      <DeviceDetailRowContainer>
                        <span className="detail-label">{AppLabels.configurationLabels.jobIdText}:</span>
                        <span className="detail-value">{jobResult.job_id}</span>
                      </DeviceDetailRowContainer>
                      <DeviceDetailRowContainer>
                        <span className="detail-label">{AppLabels.configurationLabels.statusLabel}:</span>
                        <span className="detail-value">{jobResult.status}</span>
                      </DeviceDetailRowContainer>
                      <DeviceDetailRowContainer>
                        <span className="detail-label">{AppLabels.configurationLabels.messageLabel}:</span>
                        <span className="detail-value">{jobResult.message}</span>
                      </DeviceDetailRowContainer>
                    </Box>

                    <StatusCardsContainer>
                      <StatusCard variant="uploaded">
                        <div className="status-title">{AppLabels.configurationLabels.uploadedLabel}</div>
                        <div className="status-count">{jobResult.uploaded_count} devices</div>
                      </StatusCard>
                      
                      <StatusCard variant="processed">
                        <div className="status-title">{AppLabels.configurationLabels.processedLabel}</div>
                        <div className="status-count">{jobResult.processed_rows_count} devices</div>
                      </StatusCard>
                      
                      <StatusCard variant="failed">
                        <div className="status-title">{AppLabels.configurationLabels.failedLabel}</div>
                        <div className="status-count">{jobResult.failed_count} devices</div>
                      </StatusCard>
                    </StatusCardsContainer>

                    {jobResult.errors && jobResult.errors.length > 0 && (
                      <Box className="errors-section">
                        <Typography className="errors-title">
                          {AppLabels.configurationLabels.errorsLabel}:
                        </Typography>
                        <ConfigJobStatusErrorTable>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{AppLabels.configurationLabels.rowHeader}</TableCell>
                                <TableCell>{AppLabels.configurationLabels.errorHeader}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {jobResult.errors.map((error, index) => (
                                <TableRow key={`${error.row}-${error.error}-${index}`}>
                                  <TableCell>{error.row}</TableCell>
                                  <TableCell>{error.error}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </ConfigJobStatusErrorTable>
                      </Box>
                    )}
                  </Box>
                );
              }

              // Show in-progress status
              return (
                <Box>
                  <Box className="status-section">
                    <DeviceDetailRowContainer>
                      <span className="detail-label">{AppLabels.configurationLabels.statusLabel}:</span>
                      <span className="detail-value">{jobResult.status}</span>
                    </DeviceDetailRowContainer>
                    <DeviceDetailRowContainer>
                      <span className="detail-label">{AppLabels.configurationLabels.messageLabel}:</span>
                      <span className="detail-value">{jobResult.message}</span>
                    </DeviceDetailRowContainer>
                    {jobResult.submitted_at && (
                      <DeviceDetailRowContainer>
                        <span className="detail-label">{AppLabels.configurationLabels.submittedLabel}:</span>
                        <span className="detail-value">{new Date(jobResult.submitted_at).toLocaleString()}</span>
                      </DeviceDetailRowContainer>
                    )}
                    {jobResult.updated_at && (
                      <DeviceDetailRowContainer>
                        <span className="detail-label">{AppLabels.configurationLabels.updatedLabel}:</span>
                        <span className="detail-value">{new Date(jobResult.updated_at).toLocaleString()}</span>
                      </DeviceDetailRowContainer>
                    )}
                  </Box>
                </Box>
              );
            })()}
          </ConfigJobStatusDialogContent>

          <ConfigJobStatusDialogActions>
            <ConfigPrimaryButton
              onClick={handleCloseDialog}
            >
              {AppLabels.configurationLabels.okButton}
            </ConfigPrimaryButton>
          </ConfigJobStatusDialogActions>
        </ConfigJobStatusDialog>
      </ConfigOuterContainer>
    </ConfigContainer>
  );
}
